import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Settings, ChevronRight, Loader2 } from 'lucide-react';
import SimpleStackRankTable from './SimpleStackRankTable';
import ColumnPicker from './ColumnPicker';
import GroupPicker from './GroupPicker';
import SearchFilter from './SearchFilter';
import WorkItemDetail from './WorkItemDetail';
import SprintRankService from '../services/sprintRankService';
import _ from 'lodash';

const WorkItemsTable = ({
                            workItems = [],
                            azureService,
                            onWorkItemsUpdate,
                            onRanksInitialized
                        }) => {
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const [showGroupSelector, setShowGroupSelector] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [groupBy, setGroupBy] = useState('System.IterationPath');
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [initializingGroups, setInitializingGroups] = useState(new Set());
    const [selectedItem, setSelectedItem] = useState(null);

    // Initialize services
    const sprintRankService = useMemo(
        () => new SprintRankService(azureService),
        [azureService]
    );

    // Load columns configuration
    const availableColumns = process.env.REACT_APP_AVAILABLE_COLUMNS.split(',');
    const defaultColumns = process.env.REACT_APP_DEFAULT_COLUMNS?.split(',') || [
        'System.Id',
        'System.Title',
        'System.State',
        'System.AssignedTo'
    ];

    // Initialize selected columns from localStorage or defaults
    const [selectedColumns, setSelectedColumns] = useState(() => {
        try {
            const saved = localStorage.getItem('columnPreferences');
            return saved ? JSON.parse(saved) : defaultColumns;
        } catch (error) {
            console.error('Error loading column preferences:', error);
            return defaultColumns;
        }
    });

    // Save columns to localStorage when they change
    useEffect(() => {
        try {
            localStorage.setItem('columnPreferences', JSON.stringify(selectedColumns));
        } catch (error) {
            console.error('Error saving column preferences:', error);
        }
    }, [selectedColumns]);

    const isRankingEnabled = groupBy === 'System.IterationPath' && !searchTerm;

    const getDisplayValue = useCallback((item, column) => {
        const value = _.get(item.fields, column);
        if (!value) return '-';
        if (column.endsWith('Date')) return new Date(value).toLocaleDateString();
        if (typeof value === 'object' && value !== null) return value.displayName || '-';
        return value.toString();
    }, []);

    const handleReorder = async (draggedId, targetId) => {
        if (!Array.isArray(workItems)) {
            console.error('workItems is not an array:', workItems);
            return;
        }

        const draggedItem = workItems.find(item => item.id === draggedId);
        const targetItem = workItems.find(item => item.id === targetId);

        if (!draggedItem || !targetItem) {
            console.error('Could not find dragged or target item');
            return;
        }

        try {
            // Get the current iteration's items
            const currentIteration = draggedItem.fields['System.IterationPath'];
            const iterationItems = workItems.filter(
                item => item.fields['System.IterationPath'] === currentIteration
            );

            // Get new rank from service
            const newRank = await sprintRankService.handleReorder(draggedId, targetId, iterationItems);

            // Create a new array with the updated rank
            const updatedItems = workItems.map(item => {
                if (item.id === draggedId) {
                    return {
                        ...item,
                        fields: {
                            ...item.fields,
                            'Custom.SprintRank': newRank
                        }
                    };
                }
                return item;
            });

            // Sort items, keeping iterations separated
            const sortedItems = [...updatedItems].sort((a, b) => {
                const iterationA = a.fields['System.IterationPath'];
                const iterationB = b.fields['System.IterationPath'];

                // First sort by iteration
                if (iterationA !== iterationB) {
                    return iterationA.localeCompare(iterationB);
                }

                // Within same iteration, sort by SprintRank
                const rankA = a.fields['Custom.SprintRank'] || Infinity;
                const rankB = b.fields['Custom.SprintRank'] || Infinity;
                return rankA - rankB;
            });

            // Update parent with sorted items
            onWorkItemsUpdate(sortedItems);
        } catch (error) {
            console.error('Failed to reorder:', error);
            // Trigger a refresh to ensure UI is in sync with server
            onWorkItemsUpdate(workItems);
        }
    };

    const handleToggleGroup = async (groupKey) => {
        if (!Array.isArray(workItems)) {
            console.error('workItems is not an array:', workItems);
            return;
        }

        // If group is already expanded, just collapse it
        if (expandedGroups.has(groupKey)) {
            setExpandedGroups(prev => {
                const newSet = new Set(prev);
                newSet.delete(groupKey);
                return newSet;
            });
            return;
        }

        // Otherwise, initialize ranks if needed and expand
        try {
            setInitializingGroups(prev => new Set(prev).add(groupKey));

            const iterationItems = workItems.filter(
                item => item.fields['System.IterationPath'] === groupKey
            );

            const wasInitialized = await sprintRankService.ensureIterationInitialized(
                iterationItems,
                groupKey
            );

            if (wasInitialized && onRanksInitialized) {
                // Wait for parent to refresh the data
                await onRanksInitialized(groupKey);
            }

            // Now expand the group
            setExpandedGroups(prev => new Set(prev).add(groupKey));
        } catch (error) {
            console.error('Failed to initialize ranks:', error);
        } finally {
            setInitializingGroups(prev => {
                const next = new Set(prev);
                next.delete(groupKey);
                return next;
            });
        }
    };

    const formatIterationPath = (path) => {
        const parts = path.split('\\');
        if (parts.length <= 2) return path;

        return (
            <div className="flex items-center">
                <span className="text-gray-400 text-xs">{parts.slice(0, -1).join('\\')}</span>
                <span className="text-gray-900 font-medium ml-1">{parts[parts.length - 1]}</span>
            </div>
        );
    };

    const displayItems = useMemo(() => {
        if (!Array.isArray(workItems)) {
            console.error('workItems is not an array:', workItems);
            return {};
        }

        let items = [...workItems];

        // Apply search filter if exists
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            items = items.filter(item =>
                Object.values(item.fields).some(value =>
                    String(value).toLowerCase().includes(searchLower)
                )
            );
        }

        // Group items if grouping is enabled
        if (groupBy) {
            const grouped = _.groupBy(items, item => {
                const value = _.get(item.fields, groupBy);
                if (!value) return 'Unassigned';
                return typeof value === 'object' ? value.displayName : value;
            });

            // Sort by rank within iterations if ranking is enabled
            if (isRankingEnabled) {
                Object.keys(grouped).forEach(key => {
                    grouped[key] = grouped[key].sort((a, b) => {
                        const rankA = a.fields['Custom.SprintRank'] || Infinity;
                        const rankB = b.fields['Custom.SprintRank'] || Infinity;
                        return rankA - rankB;
                    });
                });
            }

            return grouped;
        }

        return items;
    }, [workItems, searchTerm, groupBy, isRankingEnabled]);

    const renderGroupHeader = (groupKey, items) => (
        <div
            onClick={() => handleToggleGroup(groupKey)}
            className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 border-y border-gray-200 group"
        >
            <ChevronRight
                size={14}
                className={`text-gray-400 transform transition-transform mr-2 ${
                    expandedGroups.has(groupKey) ? 'rotate-90' : ''
                }`}
            />
            <div className="flex-1">
                {isRankingEnabled ? formatIterationPath(groupKey) : groupKey}
            </div>
            <div className="flex items-center gap-2">
                {initializingGroups.has(groupKey) ? (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                ) : (
                    <span className="text-xs text-gray-400 font-normal tabular-nums">
                        {items.length} {items.length === 1 ? 'item' : 'items'}
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div className="bg-white shadow-sm rounded">
            {/* Header */}
            <div className="px-3 py-2 flex justify-between items-center border-b border-gray-200">
                <h2 className="text-base font-medium text-gray-700">Work Items</h2>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowGroupSelector(!showGroupSelector)}
                        className={`
                            flex items-center gap-1 px-2 py-1 rounded text-xs
                            ${groupBy ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:text-gray-800'}
                        `}
                    >
                        <Settings size={14} />
                        Group By: {groupBy ? groupBy.split('.').pop() : 'None'}
                    </button>
                    <button
                        onClick={() => setShowColumnSelector(!showColumnSelector)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                    >
                        <Settings size={14} />
                        Columns
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-gray-200">
                <SearchFilter searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>

            {/* Content */}
            <div className="overflow-x-auto">
                {groupBy ? (
                    Object.entries(displayItems).map(([groupKey, items]) => (
                        <div key={groupKey}>
                            {renderGroupHeader(groupKey, items)}
                            {expandedGroups.has(groupKey) && Array.isArray(items) && items.length > 0 && (
                                <SimpleStackRankTable
                                    items={items}
                                    onReorder={isRankingEnabled ? handleReorder : undefined}
                                    getDisplayValue={getDisplayValue}
                                    displayColumns={selectedColumns}
                                    onItemClick={setSelectedItem}
                                    isRankingEnabled={isRankingEnabled}
                                />
                            )}
                        </div>
                    ))
                ) : (
                    Array.isArray(displayItems) && displayItems.length > 0 && (
                        <SimpleStackRankTable
                            items={displayItems}
                            onReorder={isRankingEnabled ? handleReorder : undefined}
                            getDisplayValue={getDisplayValue}
                            displayColumns={selectedColumns}
                            onItemClick={setSelectedItem}
                            isRankingEnabled={isRankingEnabled}
                        />
                    )
                )}
            </div>

            {/* Modals */}
            {showColumnSelector && (
                <ColumnPicker
                    availableColumns={availableColumns}
                    selectedColumns={selectedColumns}
                    onColumnChange={setSelectedColumns}
                    onClose={() => setShowColumnSelector(false)}
                />
            )}
            {showGroupSelector && (
                <GroupPicker
                    availableColumns={availableColumns}
                    selectedGroup={groupBy}
                    onGroupChange={setGroupBy}
                    onClose={() => setShowGroupSelector(false)}
                />
            )}

            {/* Item Detail Modal */}
            {selectedItem && (
                <WorkItemDetail
                    workItem={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    azureService={azureService}
                />
            )}

            {/* Ranking indicator */}
            {groupBy === 'System.IterationPath' && (
                <div className="fixed bottom-4 right-4 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full text-xs shadow-sm">
                    {searchTerm
                        ? 'Clear search to enable reordering'
                        : 'Select an items arrows to reorder with ↑↓ keys'
                    }
                </div>
            )}
        </div>
    );
};

export default WorkItemsTable;